import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

// Shimmer animation for skeleton effect
const shimmer = {
  animate: {
    backgroundPosition: ['200% 0', '-200% 0'],
  },
  transition: {
    duration: 2,
    ease: "linear",
    repeat: Infinity,
  }
};

// Common Loading Component with skeleton-focused variants
const CommonLoader = ({ 
  variant = 'skeleton', // Default to skeleton
  size = 'md',
  text = '',
  className = '',
  overlay = false,
  center = true,
  rows = 5, // For table/list skeletons
  columns = 4, // For table skeletons
  cards = 6 // For grid skeletons
}) => {
  // Size configurations
  const sizeConfig = {
    sm: { spinner: 'h-4 w-4', text: 'text-sm', container: 'p-2' },
    md: { spinner: 'h-8 w-8', text: 'text-base', container: 'p-4' },
    lg: { spinner: 'h-12 w-12', text: 'text-lg', container: 'p-6' },
    xl: { spinner: 'h-16 w-16', text: 'text-xl', container: 'p-8' }
  };

  const sizes = sizeConfig[size];

  // Enhanced Skeleton Line Component
  const SkeletonLine = ({ width = 'w-full', height = 'h-4', className: lineClass = '' }) => (
    <motion.div
      className={`${height} ${width} bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-md ${lineClass}`}
      style={{
        backgroundSize: '200% 100%',
      }}
      animate={{
        backgroundPosition: ['200% 0', '-200% 0'],
      }}
      transition={{
        duration: 1.5,
        ease: "linear",
        repeat: Infinity,
      }}
    />
  );

  // Skeleton Avatar Component
  const SkeletonAvatar = ({ size = 'w-10 h-10' }) => (
    <motion.div
      className={`${size} bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full`}
      style={{
        backgroundSize: '200% 100%',
      }}
      {...shimmer}
    />
  );

  // Skeleton Card Component
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start space-x-4">
        <SkeletonAvatar />
        <div className="flex-1 space-y-2">
          <SkeletonLine width="w-3/4" />
          <SkeletonLine width="w-1/2" height="h-3" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <SkeletonLine />
        <SkeletonLine width="w-5/6" />
        <SkeletonLine width="w-4/6" />
      </div>
      <div className="mt-4 flex gap-2">
        <SkeletonLine width="w-20" height="h-8" />
        <SkeletonLine width="w-20" height="h-8" />
      </div>
    </div>
  );

  // Gradient spinner component (fallback)
  const GradientSpinner = () => (
    <div className="relative">
      <div className={`${sizes.spinner} rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-spin`}>
        <div className="absolute inset-1 bg-white rounded-full"></div>
      </div>
      <div className={`absolute inset-0 ${sizes.spinner} rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-ping opacity-20`}></div>
    </div>
  );

  // Icon spinner component
  const IconSpinner = () => (
    <Loader2 className={`${sizes.spinner} animate-spin text-indigo-600`} />
  );

  // Variants
  const variants = {
    // Enhanced skeleton loader for general content
    skeleton: () => (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center space-x-4">
          <SkeletonAvatar size="w-12 h-12" />
          <div className="flex-1 space-y-2">
            <SkeletonLine width="w-1/3" height="h-5" />
            <SkeletonLine width="w-1/4" height="h-3" />
          </div>
        </div>
        <div className="space-y-3">
          <SkeletonLine />
          <SkeletonLine width="w-5/6" />
          <SkeletonLine width="w-4/6" />
          <SkeletonLine width="w-5/6" />
          <SkeletonLine width="w-3/6" />
        </div>
      </div>
    ),

    // Page skeleton with statistics cards
    pageSkeleton: () => (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <SkeletonAvatar size="w-12 h-12" />
              <div className="space-y-2">
                <SkeletonLine width="w-48" height="h-6" />
                <SkeletonLine width="w-64" height="h-4" />
              </div>
            </div>
            <div className="flex gap-3">
              <SkeletonLine width="w-24" height="h-10" />
              <SkeletonLine width="w-32" height="h-10" />
            </div>
          </div>
        </div>

        {/* Stats cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <SkeletonAvatar size="w-10 h-10" />
                <SkeletonLine width="w-16" height="h-4" />
              </div>
              <SkeletonLine width="w-20" height="h-7" />
              <SkeletonLine width="w-24" height="h-3" className="mt-2" />
            </div>
          ))}
        </div>

        {/* Content skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <TableSkeleton rows={5} />
        </div>
      </div>
    ),

    // Table skeleton with animated rows
    tableSkeleton: () => <TableSkeleton rows={rows} columns={columns} />,

    // Grid skeleton for card layouts
    gridSkeleton: () => (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(cards)].map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    ),

    // Inline skeleton for small spaces
    inlineSkeleton: () => (
      <div className="inline-flex items-center gap-3">
        <motion.div 
          className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" 
        />
        <SkeletonLine width="w-24" height="h-4" />
      </div>
    ),

    // Card skeleton
    cardSkeleton: () => <SkeletonCard />,

    // List skeleton
    listSkeleton: () => (
      <div className="space-y-3">
        {[...Array(rows)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-4">
              <SkeletonAvatar size="w-10 h-10" />
              <div className="flex-1 space-y-2">
                <SkeletonLine width="w-1/3" />
                <SkeletonLine width="w-1/4" height="h-3" />
              </div>
              <SkeletonLine width="w-20" height="h-8" />
            </div>
          </div>
        ))}
      </div>
    ),

    // Form skeleton
    formSkeleton: () => (
      <div className="space-y-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-2">
            <SkeletonLine width="w-24" height="h-4" />
            <SkeletonLine height="h-10" />
          </div>
        ))}
        <div className="flex gap-3 pt-4">
          <SkeletonLine width="w-24" height="h-10" />
          <SkeletonLine width="w-24" height="h-10" />
        </div>
      </div>
    ),

    // Stats skeleton
    statsSkeleton: () => (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="text-center">
            <SkeletonLine width="w-16 mx-auto" height="h-8" />
            <SkeletonLine width="w-20 mx-auto" height="h-4" className="mt-2" />
          </div>
        ))}
      </div>
    ),

    // Fallback to simple spinner
    default: () => (
      <div className={`flex flex-col items-center justify-center ${center ? '' : ''} ${className}`}>
        <GradientSpinner />
        {text && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`${sizes.text} text-gray-600 font-medium mt-3`}
          >
            {text}
          </motion.p>
        )}
      </div>
    ),

    // Legacy variants (kept for backward compatibility)
    page: () => variants.pageSkeleton(),
    table: () => variants.tableSkeleton(),
    grid: () => variants.gridSkeleton(),
    inline: () => variants.inlineSkeleton(),
    card: () => variants.cardSkeleton(),
    list: () => variants.listSkeleton(),
    form: () => variants.formSkeleton(),
    fullscreen: () => (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
      >
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-xl p-8 shadow-2xl max-w-md w-full mx-4"
        >
          <div className="space-y-4">
            <SkeletonAvatar size="w-16 h-16 mx-auto" />
            <div className="space-y-2">
              <SkeletonLine width="w-3/4 mx-auto" />
              <SkeletonLine width="w-1/2 mx-auto" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    ),
  };

  // Table Skeleton Component
  function TableSkeleton({ rows: tableRows = rows, columns: tableCols = columns }) {
    return (
      <div className="w-full">
        {/* Table header */}
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 rounded-t-lg">
          <div className="flex gap-4">
            {[...Array(tableCols)].map((_, i) => (
              <SkeletonLine key={i} width={`w-1/${tableCols}`} height="h-4" />
            ))}
          </div>
        </div>
        {/* Table rows */}
        {[...Array(tableRows)].map((_, rowIndex) => (
          <div key={rowIndex} className="border-b border-gray-200 px-6 py-4">
            <div className="flex gap-4 items-center">
              {[...Array(tableCols)].map((_, colIndex) => (
                <div key={colIndex} className="flex-1">
                  {colIndex === 0 ? (
                    <div className="flex items-center gap-3">
                      <SkeletonAvatar size="w-8 h-8" />
                      <SkeletonLine width="w-2/3" />
                    </div>
                  ) : (
                    <SkeletonLine width={colIndex === tableCols - 1 ? "w-20" : "w-full"} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render with overlay if needed
  if (overlay) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-40 flex items-center justify-center">
        {variants[variant] ? variants[variant]() : variants.skeleton()}
      </div>
    );
  }

  // Render the selected variant (default to skeleton)
  return variants[variant] ? variants[variant]() : variants.skeleton();
};

// Export specific loader types for convenience
export const PageLoader = (props) => <CommonLoader variant="pageSkeleton" {...props} />;
export const TableLoader = (props) => <CommonLoader variant="tableSkeleton" {...props} />;
export const GridLoader = (props) => <CommonLoader variant="gridSkeleton" {...props} />;
export const InlineLoader = (props) => <CommonLoader variant="inlineSkeleton" {...props} />;
export const CardLoader = (props) => <CommonLoader variant="cardSkeleton" {...props} />;
export const ListLoader = (props) => <CommonLoader variant="listSkeleton" {...props} />;
export const FormLoader = (props) => <CommonLoader variant="formSkeleton" {...props} />;
export const FullscreenLoader = (props) => <CommonLoader variant="fullscreen" {...props} />;
export const SkeletonLoader = (props) => <CommonLoader variant="skeleton" {...props} />;

// Legacy exports for backward compatibility
export const ButtonLoader = (props) => <CommonLoader variant="inline" {...props} />;

// Loading state wrapper component
export const LoadingState = ({ loading, children, variant = 'skeleton', text = '', ...props }) => {
  if (loading) {
    return <CommonLoader variant={variant} text={text} {...props} />;
  }
  return children;
};

// Async component wrapper with loading state
export const AsyncWrapper = ({ loading, error, empty, children, emptyText = 'No data found', errorText = 'An error occurred' }) => {
  if (loading) {
    return <CommonLoader variant="pageSkeleton" />;
  }
  
  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600">{errorText}</p>
        </div>
      </div>
    );
  }
  
  if (empty) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-600">{emptyText}</p>
        </div>
      </div>
    );
  }
  
  return children;
};

export default CommonLoader;